import { useCallback, useEffect, useState } from 'react'
import { isPwaDisplayMode } from './useAppShellMode'

/** Chromium `beforeinstallprompt` event (not in all TS lib.dom versions). */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export type PwaPlatform = 'ios' | 'android' | 'desktop'

export function detectPwaPlatform(): PwaPlatform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent || ''
  const isIos =
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (isIos) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

export type PwaInstallState = {
  platform: PwaPlatform
  /** Already running as installed PWA / home-screen app */
  isInstalled: boolean
  /** Deferred Chromium install prompt is available */
  canPromptInstall: boolean
  /** True while native install UI is open */
  installing: boolean
  /** Outcome of last prompt attempt, if any */
  lastOutcome: 'accepted' | 'dismissed' | null
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
}

/**
 * Captures `beforeinstallprompt` and reports install / platform state
 * for the public /install landing page.
 */
export function usePwaInstall(): PwaInstallState {
  const [platform] = useState<PwaPlatform>(() => detectPwaPlatform())
  const [isInstalled, setIsInstalled] = useState(() => isPwaDisplayMode())
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installing, setInstalling] = useState(false)
  const [lastOutcome, setLastOutcome] = useState<'accepted' | 'dismissed' | null>(null)

  useEffect(() => {
    const syncInstalled = () => setIsInstalled(isPwaDisplayMode())
    syncInstalled()

    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setDeferred(null)
      setIsInstalled(true)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)

    const standMqs = [
      window.matchMedia('(display-mode: standalone)'),
      window.matchMedia('(display-mode: fullscreen)'),
      window.matchMedia('(display-mode: minimal-ui)'),
    ]
    standMqs.forEach((mq) => mq.addEventListener('change', syncInstalled))

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
      standMqs.forEach((mq) => mq.removeEventListener('change', syncInstalled))
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable' as const
    setInstalling(true)
    try {
      await deferred.prompt()
      const { outcome } = await deferred.userChoice
      setLastOutcome(outcome)
      setDeferred(null)
      if (outcome === 'accepted') setIsInstalled(true)
      return outcome
    } catch {
      return 'unavailable' as const
    } finally {
      setInstalling(false)
    }
  }, [deferred])

  return {
    platform,
    isInstalled,
    canPromptInstall: Boolean(deferred) && !isInstalled,
    installing,
    lastOutcome,
    promptInstall,
  }
}
